<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  campaign: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['close']);

const store = useStore();
const isActionLoading = ref(false);
const searchQuery = ref('');
const activeFilter = ref('all'); // 'all' | 'sent' | 'skipped' | 'failed'

const currentCampaign = computed(() => {
  const all = store.getters['campaigns/getAllCampaigns'] || [];
  return all.find(c => c.id === props.campaign?.id) || props.campaign;
});

const checkpointTag = computed(() => {
  const rules = currentCampaign.value?.trigger_rules || {};
  const params = currentCampaign.value?.template_params || {};
  const custom = rules.checkpoint_label || params.checkpoint_label;
  if (custom && custom.trim()) return custom.trim();
  if (currentCampaign.value?.title) {
    const slug = currentCampaign.value.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    return `campanha-whats-${slug}`;
  }
  return 'Automático';
});

const report = computed(() => {
  return currentCampaign.value?.trigger_rules?.report || {
    total_contacts: 0,
    sent_count: 0,
    skipped_count: 0,
    failed_count: 0,
    pending_count: 0,
    duration_seconds: 0,
    status: currentCampaign.value?.campaign_status || 'completed',
    logs: []
  };
});

const isRunning = computed(() => {
  return currentCampaign.value?.campaign_status === 'processing' || report.value?.status === 'running';
});

const isPaused = computed(() => {
  return currentCampaign.value?.campaign_status === 'paused' || report.value?.status === 'paused';
});

const isCompleted = computed(() => {
  return currentCampaign.value?.campaign_status === 'completed' || report.value?.status === 'completed';
});

const statusBadge = computed(() => {
  if (isRunning.value) {
    return {
      label: 'Em Disparo Ativo',
      class: 'bg-teal-500/15 text-teal-700 dark:text-teal-300 border border-teal-500/30 font-bold',
      dotClass: 'bg-teal-500 animate-ping'
    };
  }
  if (isPaused.value) {
    return {
      label: 'Disparo Pausado',
      class: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/30 font-bold',
      dotClass: 'bg-amber-500'
    };
  }
  if (isCompleted.value) {
    return {
      label: 'Campanha Concluída',
      class: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30 font-bold',
      dotClass: 'bg-emerald-500'
    };
  }
  return {
    label: 'Agendada',
    class: 'bg-[#2B8EFF]/15 text-[#2B8EFF] dark:text-[#52A3FF] border border-[#2B8EFF]/30 font-bold',
    dotClass: 'bg-[#2B8EFF]'
  };
});

const formattedDuration = computed(() => {
  const totalSeconds = report.value?.duration_seconds || 0;
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const mm = String(minutes).padStart(2, '0');
  const ss = String(seconds).padStart(2, '0');
  if (hours > 0) {
    return `${String(hours).padStart(2, '0')}h ${mm}m ${ss}s`;
  }
  return `${mm}m ${ss}s`;
});

const progressPercentage = computed(() => {
  const total = report.value?.total_contacts || 0;
  if (!total) return isCompleted.value ? 100 : 0;
  const sent = report.value?.sent_count || 0;
  return Math.min(Math.round((sent / total) * 100), 100);
});

const avgDelay = computed(() => {
  const logs = report.value?.logs || [];
  const delays = logs.filter(l => l.status === 'sent' && l.delay_seconds > 0).map(l => l.delay_seconds);
  if (!delays.length) return '30s - 180s';
  const sum = delays.reduce((a, b) => a + b, 0);
  return `${Math.round(sum / delays.length)}s`;
});

const filteredLogs = computed(() => {
  let logs = report.value?.logs || [];
  if (activeFilter.value === 'sent') {
    logs = logs.filter(l => l.status === 'sent');
  } else if (activeFilter.value === 'skipped') {
    logs = logs.filter(l => l.status === 'skipped');
  } else if (activeFilter.value === 'failed') {
    logs = logs.filter(l => l.status === 'failed');
  }

  if (searchQuery.value.trim()) {
    const q = searchQuery.value.toLowerCase().trim();
    logs = logs.filter(l =>
      (l.contact_name && l.contact_name.toLowerCase().includes(q)) ||
      (l.phone_number && l.phone_number.includes(q)) ||
      (l.message_preview && l.message_preview.toLowerCase().includes(q))
    );
  }
  return logs;
});

const handlePause = async () => {
  isActionLoading.value = true;
  try {
    await store.dispatch('campaigns/update', {
      id: currentCampaign.value.id,
      campaign_status: 'paused'
    });
    useAlert('Campanha pausada com sucesso!');
    await store.dispatch('campaigns/get', { campaignType: 'one_off' });
  } catch (err) {
    useAlert('Erro ao pausar campanha.');
  } finally {
    isActionLoading.value = false;
  }
};

const handleResume = async () => {
  isActionLoading.value = true;
  try {
    await store.dispatch('campaigns/update', {
      id: currentCampaign.value.id,
      campaign_status: 'active'
    });
    useAlert('Campanha retomada com sucesso!');
    await store.dispatch('campaigns/get', { campaignType: 'one_off' });
  } catch (err) {
    useAlert('Erro ao retomar campanha.');
  } finally {
    isActionLoading.value = false;
  }
};

const handleCancelCampaign = async () => {
  if (!confirm('Deseja realmente cancelar esta campanha? Os disparos restantes serão interrompidos.')) {
    return;
  }
  isActionLoading.value = true;
  try {
    await store.dispatch('campaigns/update', {
      id: currentCampaign.value.id,
      campaign_status: 'completed'
    });
    useAlert('Campanha cancelada com sucesso!');
    await store.dispatch('campaigns/get', { campaignType: 'one_off' });
  } catch (err) {
    useAlert('Erro ao cancelar campanha.');
  } finally {
    isActionLoading.value = false;
  }
};

let pollInterval = null;

onMounted(() => {
  pollInterval = setInterval(async () => {
    if (isRunning.value) {
      await store.dispatch('campaigns/get', { campaignType: 'one_off' });
    }
  }, 3000);
});

onUnmounted(() => {
  if (pollInterval) clearInterval(pollInterval);
});

const handleClose = () => {
  emit('close');
};
</script>

<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in"
    @click.self="handleClose"
  >
    <div class="flex flex-col w-full max-w-4xl max-h-[90vh] bg-white dark:bg-n-solid-2 rounded-2xl shadow-2xl border border-n-weak overflow-hidden">
      <!-- MODAL HEADER -->
      <div class="flex items-center justify-between px-6 py-4 border-b border-n-weak bg-n-alpha-1">
        <div class="flex items-center gap-3">
          <div class="size-10 rounded-xl bg-[#2B8EFF]/15 text-[#2B8EFF] flex items-center justify-center font-bold">
            <svg class="size-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <div>
            <h2 class="text-lg font-bold text-n-slate-12">
              {{ currentCampaign.title }}
            </h2>
            <div class="text-xs text-n-slate-11 flex items-center gap-2 flex-wrap mt-0.5">
              <span>Caixa: <b class="text-n-slate-12">{{ currentCampaign.inbox?.name || 'WhatsApp' }}</b></span>
              <span>•</span>
              <span>Status: <span class="font-semibold" :class="statusBadge.class.split(' ')[1]">{{ statusBadge.label }}</span></span>
              <span>•</span>
              <span class="font-mono text-[#2B8EFF] bg-blue-500/10 px-1.5 py-0.5 rounded border border-blue-500/20">🏷️ {{ checkpointTag }}</span>
            </div>
          </div>
        </div>

        <div class="flex items-center gap-2.5">
          <!-- Botão Pausar -->
          <button
            v-if="isRunning"
            type="button"
            class="px-3.5 py-1.5 rounded-lg text-xs font-semibold bg-amber-600 hover:bg-amber-700 text-white flex items-center gap-1.5 transition-all shadow-sm cursor-pointer"
            :disabled="isActionLoading"
            @click="handlePause"
          >
            <svg class="size-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Pausar</span>
          </button>

          <!-- Botão Retomar -->
          <button
            v-if="isPaused"
            type="button"
            class="px-3.5 py-1.5 rounded-lg text-xs font-semibold bg-teal-600 hover:bg-teal-700 text-white flex items-center gap-1.5 transition-all shadow-sm cursor-pointer"
            :disabled="isActionLoading"
            @click="handleResume"
          >
            <svg class="size-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Retomar</span>
          </button>

          <!-- Botão Cancelar Campanha -->
          <button
            v-if="!isCompleted"
            type="button"
            class="px-3 py-1.5 rounded-lg text-xs font-semibold bg-rose-500/15 hover:bg-rose-500/25 text-rose-600 dark:text-rose-400 border border-rose-500/30 flex items-center gap-1 transition-all cursor-pointer"
            :disabled="isActionLoading"
            @click="handleCancelCampaign"
          >
            <svg class="size-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
            <span>Cancelar</span>
          </button>

          <!-- Botão Fechar -->
          <button
            type="button"
            class="p-2 rounded-xl text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-all cursor-pointer"
            @click="handleClose"
          >
            <svg class="size-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      <!-- MODAL BODY (SCROLLABLE) -->
      <div class="flex-1 overflow-y-auto p-6 flex flex-col gap-6 text-n-slate-12">
        <!-- PROGRESS BAR -->
        <div class="p-4 rounded-2xl bg-gradient-to-br from-[#2B8EFF]/10 via-[#2B8EFF]/5 to-slate-500/5 dark:from-[#2B8EFF]/20 dark:via-blue-950/20 dark:to-slate-900/30 border border-[#2B8EFF]/30 flex flex-col gap-2.5">
          <div class="flex items-center justify-between text-xs">
            <span class="font-bold text-n-slate-12 flex items-center gap-1.5">
              <span>⚡ Andamento Geral do Disparo</span>
            </span>
            <span class="font-bold text-[#2B8EFF] dark:text-[#52A3FF]">
              {{ report.sent_count }} de {{ report.total_contacts }} conversas entregues ({{ progressPercentage }}%)
            </span>
          </div>

          <div class="w-full bg-n-weak/60 h-3.5 rounded-full overflow-hidden p-0.5">
            <div
              class="bg-gradient-to-r from-[#2B8EFF] to-emerald-500 h-full transition-all duration-700 rounded-full"
              :style="{ width: `${progressPercentage}%` }"
            />
          </div>
        </div>

        <!-- 4 KPI TILES -->
        <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div class="bg-n-alpha-1 p-4 rounded-2xl border border-n-weak shadow-sm flex flex-col justify-between">
            <span class="text-xs text-n-slate-11 font-medium">🎯 Total na Cadência</span>
            <div class="flex items-baseline justify-between mt-2">
              <span class="text-2xl font-black text-n-slate-12">{{ report.total_contacts }}</span>
              <span class="text-[11px] text-n-slate-11">contatos</span>
            </div>
          </div>

          <div class="bg-n-alpha-1 p-4 rounded-2xl border border-emerald-500/30 shadow-sm flex flex-col justify-between">
            <span class="text-xs text-emerald-600 dark:text-emerald-400 font-medium">✉️ Disparados c/ Sucesso</span>
            <div class="flex items-baseline justify-between mt-2">
              <span class="text-2xl font-black text-emerald-600 dark:text-emerald-400">{{ report.sent_count }}</span>
              <span class="text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">{{ progressPercentage }}%</span>
            </div>
          </div>

          <div class="bg-n-alpha-1 p-4 rounded-2xl border border-amber-500/30 shadow-sm flex flex-col justify-between">
            <span class="text-xs text-amber-600 dark:text-amber-400 font-medium">⏳ Restantes / Fila</span>
            <div class="flex items-baseline justify-between mt-2">
              <span class="text-2xl font-black text-amber-600 dark:text-amber-400">{{ report.pending_count }}</span>
              <span class="text-[11px] text-amber-600/80 dark:text-amber-400/80">{{ report.skipped_count }} ignorados</span>
            </div>
          </div>

          <div class="bg-n-alpha-1 p-4 rounded-2xl border border-[#2B8EFF]/30 shadow-sm flex flex-col justify-between">
            <span class="text-xs text-[#2B8EFF] dark:text-[#52A3FF] font-medium">⏱️ Duração Total</span>
            <div class="flex items-baseline justify-between mt-2">
              <span class="text-lg font-black text-[#2B8EFF] dark:text-[#52A3FF]">{{ formattedDuration }}</span>
              <span class="text-[10px] text-[#2B8EFF] font-mono">Méd: {{ avgDelay }}</span>
            </div>
          </div>
        </div>

        <!-- LOGS DAS CONVERSAS -->
        <div class="flex flex-col gap-3">
          <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <h3 class="text-xs font-bold uppercase tracking-wider text-n-slate-11">
              Registro Detalhado por Conversa ({{ filteredLogs.length }})
            </h3>

            <!-- Filtros e Busca -->
            <div class="flex items-center gap-2 flex-wrap">
              <div class="flex rounded-lg bg-n-alpha-2 p-0.5 border border-n-weak text-xs">
                <button
                  type="button"
                  class="px-2.5 py-1 rounded-md transition-colors cursor-pointer"
                  :class="activeFilter === 'all' ? 'bg-white dark:bg-n-solid-3 font-semibold text-n-slate-12 shadow-sm' : 'text-n-slate-11'"
                  @click="activeFilter = 'all'"
                >
                  Todos ({{ report.logs?.length || 0 }})
                </button>
                <button
                  type="button"
                  class="px-2.5 py-1 rounded-md transition-colors cursor-pointer"
                  :class="activeFilter === 'sent' ? 'bg-white dark:bg-n-solid-3 font-semibold text-emerald-600 dark:text-emerald-400 shadow-sm' : 'text-n-slate-11'"
                  @click="activeFilter = 'sent'"
                >
                  Enviados ({{ report.sent_count || 0 }})
                </button>
                <button
                  type="button"
                  class="px-2.5 py-1 rounded-md transition-colors cursor-pointer"
                  :class="activeFilter === 'skipped' ? 'bg-white dark:bg-n-solid-3 font-semibold text-amber-600 dark:text-amber-400 shadow-sm' : 'text-n-slate-11'"
                  @click="activeFilter = 'skipped'"
                >
                  Ignorados ({{ report.skipped_count || 0 }})
                </button>
              </div>

              <input
                type="text"
                v-model="searchQuery"
                placeholder="Buscar conversa..."
                class="text-xs px-3 py-1.5 bg-n-alpha-1 border border-n-weak rounded-lg outline-none focus:ring-1 focus:ring-[#2B8EFF] w-36 sm:w-48"
              />
            </div>
          </div>

          <!-- LISTA DE CARDS -->
          <div class="max-h-80 overflow-y-auto rounded-2xl border border-n-weak bg-n-alpha-1 divide-y divide-n-weak shadow-inner">
            <div
              v-for="(log, idx) in filteredLogs"
              :key="idx"
              class="p-4 flex flex-col gap-2.5 hover:bg-n-alpha-2 transition-colors"
            >
              <div class="flex items-center justify-between text-xs flex-wrap gap-2">
                <div class="flex items-center gap-2.5">
                  <div class="size-7 rounded-full bg-[#2B8EFF]/15 text-[#2B8EFF] dark:text-[#52A3FF] flex items-center justify-center font-bold text-xs">
                    {{ log.contact_name ? log.contact_name.charAt(0).toUpperCase() : 'C' }}
                  </div>
                  <div>
                    <span class="font-bold text-n-slate-12 text-sm">{{ log.contact_name }}</span>
                    <span class="text-n-slate-11 font-mono text-xs ml-1.5">({{ log.phone_number }})</span>
                  </div>
                </div>

                <div class="flex items-center gap-2">
                  <span v-if="log.delay_seconds" class="text-xs text-n-slate-11 bg-n-alpha-2 px-2.5 py-0.5 rounded-full font-mono">
                    ⏱️ Delay: {{ log.delay_seconds }}s
                  </span>
                  <span
                    class="px-3 py-0.5 rounded-full text-xs font-bold"
                    :class="log.status === 'sent' ? 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30' : 'bg-slate-500/20 text-slate-700 dark:text-slate-300 border border-slate-500/30'"
                  >
                    {{ log.status === 'sent' ? '✓ Enviado' : 'Ignorado' }}
                  </span>
                  <span class="text-n-slate-11 text-xs font-mono">{{ log.sent_at }}</span>
                </div>
              </div>

              <!-- Prévia da mensagem ou motivo de ignorado -->
              <div v-if="log.message_preview" class="text-xs text-n-slate-11 bg-white/80 dark:bg-n-solid-3 p-3 rounded-xl border border-n-weak flex flex-col gap-1 shadow-sm">
                <span class="text-[10px] font-bold text-[#2B8EFF] uppercase tracking-wide">Mensagem Entregue (IA):</span>
                <p class="italic text-n-slate-12 leading-relaxed">"{{ log.message_preview }}"</p>
              </div>
              <div v-else-if="log.reason" class="text-xs text-amber-700 dark:text-amber-300 bg-amber-500/10 px-3.5 py-2 rounded-xl border border-amber-500/30 flex items-center gap-2">
                <span>⚠️ <b>Motivo do Filtro:</b></span>
                <span>{{ log.reason }}</span>
              </div>
            </div>

            <div v-if="filteredLogs.length === 0" class="p-12 text-center text-xs text-n-slate-11">
              Nenhum registro encontrado para este filtro.
            </div>
          </div>
        </div>
      </div>

      <!-- FOOTER -->
      <div class="px-6 py-4 border-t border-n-weak bg-n-alpha-1 flex justify-end">
        <button
          type="button"
          class="px-5 py-2 rounded-xl text-xs font-bold bg-slate-200 dark:bg-n-solid-3 hover:bg-slate-300 dark:hover:bg-n-solid-4 text-n-slate-12 transition-all shadow-sm cursor-pointer"
          @click="handleClose"
        >
          Fechar Relatório
        </button>
      </div>
    </div>
  </div>
</template>
